import { useEffect } from "react";

/**
 * Site-wide video playback manager, mounted once in the app shell:
 *
 *  1. Only one video plays at a time — when any <video> starts, every other
 *     playing <video> on the page is paused (media `play` events don't
 *     bubble, so the listener uses the capture phase).
 *  2. A playing video that scrolls fully out of view is paused (it stops
 *     being observed once paused, and is re-observed on its next play).
 *
 * Operating on the DOM rather than per-component wiring covers every video
 * surface — feed posts, the hero carousel, composer previews — including
 * future ones, automatically.
 */
export function useSingleActiveVideo() {
	useEffect(() => {
		const observer =
			typeof IntersectionObserver !== "undefined"
				? new IntersectionObserver((entries) => {
						for (const entry of entries) {
							const video = entry.target as HTMLVideoElement;
							if (!entry.isIntersecting && !video.paused) {
								video.pause();
							}
						}
					})
				: null;

		const onPlay = (e: Event) => {
			const target = e.target;
			if (!(target instanceof HTMLVideoElement)) return;
			for (const video of document.querySelectorAll("video")) {
				if (video !== target && !video.paused) video.pause();
			}
			observer?.observe(target);
		};
		const onStop = (e: Event) => {
			if (e.target instanceof HTMLVideoElement) {
				observer?.unobserve(e.target);
			}
		};

		document.addEventListener("play", onPlay, true);
		document.addEventListener("pause", onStop, true);
		document.addEventListener("emptied", onStop, true);
		return () => {
			document.removeEventListener("play", onPlay, true);
			document.removeEventListener("pause", onStop, true);
			document.removeEventListener("emptied", onStop, true);
			observer?.disconnect();
		};
	}, []);
}
